import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { useStagePool } from "./hooks";
import { type SelectorSize } from "./sizes";
import { selectionToStageEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import type { Omittable } from "@salestrak/ui";

export type StageSelectorProps = Omittable<{
  /** Workflow to load stages from */
  workflowId: string | null | undefined;
  /** Currently selected stage ID */
  value?: string | null;
  /** Called when a stage is selected */
  onValueChange?: (stageId: string) => void;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  /** Render a static, non-dimmed value (no picker) — view/comment tier. */
  readOnly?: boolean;
  children?: React.ReactNode;
}>;

export function StageSelector({
  workflowId,
  value,
  onValueChange,
  variant,
  placeholder = "Select stage...",
  size,
  disabled,
  readOnly,
  children,
}: StageSelectorProps) {
  const { pool } = useStagePool(workflowId);

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 1,
      searchPlaceholder: "Search stages...",
      emptyMessage: "No stages found",
      noItemsMessage: "This workflow has no stages",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    const entity = pool.items.find((item) => item.id === value);
    if (!entity) return [];
    return [{ id: value, poolId: pool.id, entity }];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const selected = next[0];
      if (selected) onValueChange?.(selected.id);
    },
    [onValueChange],
  );

  const toEntity = useCallback(
    (sel: EntitySelection) => selectionToStageEntity(sel, workflowId ?? undefined),
    [workflowId],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onValueChange={handleValueChange}>
      {children ?? (
        <>
          <SelectionEntityTrigger
            toEntity={toEntity}
            placeholder={placeholder}
            title="Stage"
            size={size}
            disabled={disabled}
            readOnly={readOnly}
          />
          <EntitySelectorContent>
            <EntitySelectorInput />
            <EntitySelectorPool value={pool.id} />
            <EntitySelectorEmpty>No stages found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
